import { WebPageEvents } from '@griply/types';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useBeginDesktopSignInMutation } from 'shared/hooks/queries/use-begin-desktop-sign-in-mutation';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { QueryParamOptions } from 'shared/lib/react-router-dom';
import { getDesktopSignInCallbackUrl } from 'shared/utils/get-desktop-sign-in-urls';

import * as Styled from './desktop-connected-view.style';

export const DesktopConnectedView: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const challenge = searchParams.get(QueryParamOptions.DesktopChallenge);
  const { mutate, isError } = useBeginDesktopSignInMutation();
  const hasStartedRef = useRef(false);
  useTrackPageVisitEvent(WebPageEvents.DesktopConnected, undefined);

  const hasFailed = isError || !challenge;

  const handOffToApp = useCallback(
    (code: string) => {
      if (!challenge) {
        return;
      }
      window.location.href = getDesktopSignInCallbackUrl(code, challenge);
    },
    [challenge],
  );

  useEffect(() => {
    if (!challenge || hasStartedRef.current) {
      return;
    }

    hasStartedRef.current = true;
    mutate(challenge, { onSuccess: handOffToApp });
  }, [challenge, handOffToApp, mutate]);

  return (
    <Styled.Container>
      <Styled.Content>
        <Styled.Logo
          src="/griply-logo/griply-logo.png"
          srcSet="/griply-logo/griply-logo@2x.png 2x, /griply-logo/griply-logo@3x.png 3x"
          alt="Griply"
        />

        <Typography variant="h3" textAlign="center">
          {t(
            hasFailed
              ? 'pages.desktop-connected.failed.title'
              : 'pages.desktop-connected.title',
          )}
        </Typography>

        <Styled.Description>
          {t(
            hasFailed
              ? 'pages.desktop-connected.failed.description'
              : 'pages.desktop-connected.description',
          )}
        </Styled.Description>
      </Styled.Content>
    </Styled.Container>
  );
};
